import { FunctionComponent, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    FaceValidationMeasurement,
    useLabelFaceValidationMeasurementMutation,
} from '@/api/v2/faceValidation';
import {
    Thresholds,
    Verdict,
    evaluate,
} from '@/pageContents/faceValidationContent/thresholds';

const VERDICT_LABEL: Record<Verdict, string> = {
    APPROVED: 'Würde angenommen',
    NO_FACE_DETECTED: 'Kein Gesicht erkannt',
    FACE_OCCLUDED: 'Gesicht verdeckt',
    LOW_IMAGE_QUALITY: 'Bildqualität zu niedrig',
};

type Filter = 'all' | 'rejected' | 'approved' | 'unlabelled' | 'disagreement';

const FILTER_LABEL: Record<Filter, string> = {
    all: 'Alle',
    rejected: 'Würden abgelehnt',
    approved: 'Würden angenommen',
    unlabelled: 'Noch nicht bewertet',
    disagreement: 'Abweichung zu deiner Bewertung',
};

type PhotoReviewProps = {
    measurements: FaceValidationMeasurement[];
    thresholds: Thresholds;
};

/**
 * #1870. Shows the photos behind the numbers, so a rejection can be judged rather than just counted.
 *
 * Without this the calibration is blind: the share of rejections says nothing about whether those
 * rejections were right. Marking a photo as fine or unusable is what produces the error rate the
 * go-live gate is written against.
 *
 * The pictures are the ones already stored for the keycard, shown here and not copied anywhere. No
 * names are displayed - the question is whether the photo is usable, and who it belongs to does not
 * help answer it.
 */
export const PhotoReview: FunctionComponent<PhotoReviewProps> = ({
    measurements,
    thresholds,
}) => {
    const [filter, setFilter] = useState<Filter>('rejected');
    const [labelMeasurement, { isLoading }] =
        useLabelFaceValidationMeasurementMutation();

    const shown = useMemo(() => {
        const withVerdict = measurements
            .filter((m) => m.imageUrl)
            .map((m) => ({ m, verdict: evaluate(m, thresholds) }));

        switch (filter) {
            case 'rejected':
                return withVerdict.filter((x) => x.verdict !== 'APPROVED');
            case 'approved':
                return withVerdict.filter((x) => x.verdict === 'APPROVED');
            case 'unlabelled':
                return withVerdict.filter((x) => !x.m.humanLabel);
            case 'disagreement':
                // The two cases worth the most attention: a photo somebody called fine that the
                // check throws out, and one called unusable that it lets through.
                return withVerdict.filter(
                    (x) =>
                        (x.m.humanLabel === 'GOOD' &&
                            x.verdict !== 'APPROVED') ||
                        (x.m.humanLabel === 'BAD' && x.verdict === 'APPROVED')
                );
            default:
                return withVerdict;
        }
    }, [measurements, thresholds, filter]);

    const withImages = measurements.filter((m) => m.imageUrl).length;

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent='space-between'
                    alignItems={{ sm: 'center' }}
                    spacing={2}
                    sx={{ mb: 1 }}
                >
                    <Box>
                        <Typography variant='h6'>Fotos ansehen</Typography>
                        <Typography variant='caption' color='text.secondary'>
                            Bewerte, ob ein Foto in Ordnung ist. Erst daraus
                            ergibt sich, wie viele Ablehnungen zu Unrecht
                            passieren.
                        </Typography>
                    </Box>

                    <TextField
                        select
                        size='small'
                        label='Auswahl'
                        value={filter}
                        onChange={(e) => setFilter(e.target.value as Filter)}
                        sx={{ minWidth: 260 }}
                    >
                        {(Object.keys(FILTER_LABEL) as Filter[]).map((f) => (
                            <MenuItem key={f} value={f}>
                                {FILTER_LABEL[f]}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>

                {withImages === 0 && (
                    <Typography color='text.secondary' sx={{ mt: 2 }}>
                        Zu diesen Messwerten sind keine Fotos hinterlegt.
                    </Typography>
                )}

                {withImages > 0 && shown.length === 0 && (
                    <Typography color='text.secondary' sx={{ mt: 2 }}>
                        Keine Fotos in dieser Auswahl.
                    </Typography>
                )}

                <Box
                    sx={{
                        mt: 2,
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            lg: 'repeat(3, 1fr)',
                        },
                    }}
                >
                    {shown.slice(0, 60).map(({ m, verdict }) => {
                        const disagrees =
                            (m.humanLabel === 'GOOD' &&
                                verdict !== 'APPROVED') ||
                            (m.humanLabel === 'BAD' && verdict === 'APPROVED');

                        return (
                            <Card
                                key={m.id}
                                variant='outlined'
                                sx={{
                                    borderColor: disagrees
                                        ? 'error.main'
                                        : undefined,
                                    borderWidth: disagrees ? 2 : 1,
                                }}
                            >
                                <Box
                                    component='img'
                                    src={m.imageUrl ?? ''}
                                    alt='Identifikationsfoto'
                                    loading='lazy'
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '3 / 4',
                                        objectFit: 'cover',
                                        display: 'block',
                                        bgcolor: 'grey.100',
                                    }}
                                />
                                <CardContent sx={{ pb: 1.5 }}>
                                    <Chip
                                        size='small'
                                        label={VERDICT_LABEL[verdict]}
                                        color={
                                            verdict === 'APPROVED'
                                                ? 'success'
                                                : 'warning'
                                        }
                                        variant='outlined'
                                        sx={{ mb: 1 }}
                                    />

                                    <Typography
                                        variant='caption'
                                        display='block'
                                        color='text.secondary'
                                        sx={{
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        Schärfe {m.sharpness?.toFixed(0) ?? '–'}{' '}
                                        · Helligkeit{' '}
                                        {m.brightness?.toFixed(0) ?? '–'}
                                        {m.occluded
                                            ? ` · verdeckt ${m.occludedConfidence?.toFixed(0)}%`
                                            : ''}
                                        {m.sunglasses
                                            ? ` · Sonnenbrille ${m.sunglassesConfidence?.toFixed(0)}%`
                                            : ''}
                                    </Typography>

                                    <Stack
                                        direction='row'
                                        spacing={1}
                                        sx={{ mt: 1.5 }}
                                    >
                                        <Tooltip title='Das Foto ist in Ordnung'>
                                            <Button
                                                size='small'
                                                variant={
                                                    m.humanLabel === 'GOOD'
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                color='success'
                                                disabled={isLoading}
                                                onClick={() =>
                                                    labelMeasurement({
                                                        id: m.id,
                                                        label: 'GOOD',
                                                    })
                                                }
                                            >
                                                In Ordnung
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title='Das Foto ist tatsächlich unbrauchbar'>
                                            <Button
                                                size='small'
                                                variant={
                                                    m.humanLabel === 'BAD'
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                color='warning'
                                                disabled={isLoading}
                                                onClick={() =>
                                                    labelMeasurement({
                                                        id: m.id,
                                                        label: 'BAD',
                                                    })
                                                }
                                            >
                                                Unbrauchbar
                                            </Button>
                                        </Tooltip>
                                    </Stack>

                                    {disagrees && (
                                        <Typography
                                            variant='caption'
                                            color='error'
                                            display='block'
                                            sx={{ mt: 1 }}
                                        >
                                            {m.humanLabel === 'GOOD'
                                                ? 'Du hältst es für in Ordnung — die Prüfung würde es ablehnen.'
                                                : 'Du hältst es für unbrauchbar — die Prüfung würde es durchlassen.'}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>

                {shown.length > 60 && (
                    <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mt: 2, display: 'block' }}
                    >
                        {shown.length} Fotos in dieser Auswahl, die ersten 60
                        werden gezeigt.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};
